// HealthSherpaInscribirView — generar el enlace de inscripción de HealthSherpa
// para un cliente, de forma que la póliza NAZCA EMPAREJADA.
//
// POR QUE EXISTE ESTA PANTALLA. Emparejar hacia atrás es casi imposible:
// HealthSherpa no manda ningún dato personal con el estado de una póliza, así
// que no hay nada que comparar con el CRM. Lissette lo probó con sus 21
// pólizas ya vendidas y salieron CERO.
//
// La documentación de ellos dice cuál es la vía buena, y es al revés: que el
// CRM mande SU identificador cuando ARRANCA la inscripción —
// «external_id: partner-supplied identifier echoed in the response and used
// for CRM correlation». Deskte crea la sesión, el cliente se inscribe por el
// enlace, y cuando esa póliza aparece en el estado viene con ese
// identificador puesto. Nadie vuelve a emparejar nada.
//
// PRERELLENADO (2026-10-27). El enlace además va con la ficha del cliente ya
// puesta: nombre, fecha de nacimiento, contacto, dirección, hogar. Los
// nombres exactos de esos campos salen de su contrato OpenAPI, no de
// adivinar. La pantalla enseña la LISTA de lo que viaja antes de que el
// agente marque el consentimiento, para que pueda decírselo al cliente.
//
// LO QUE ESTA PANTALLA NO HACE: inscribir dentro de Deskte. No es una
// limitación nuestra — la aplicación ACA está regulada por CMS y tiene que
// correr en una plataforma aprobada. Lo que se genera es un enlace a SU
// formulario.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2, HeartPulse, Check, Copy, ExternalLink, AlertTriangle, UserPlus } from "lucide-react";
import {
  listClients,
  createHealthSherpaEnrollmentSession,
  getHealthSherpaEnrollmentSessions,
  getHealthSherpaConnection,
} from "../api.js";
import { toast } from "./Toast.jsx";
import { EVENTO_PENDIENTE, PENDIENTE_HEALTHSHERPA, tomarPendiente } from "../enlacesDeLaFicha.js";

// El servidor manda las etiquetas del prerellenado en español, porque es el
// idioma de la casa; aquí se traducen para la interfaz en inglés. Una
// etiqueta que no esté en esta tabla se enseña tal cual en vez de perderse.
const PRERELLENO_EN = {
  "nombre": "name",
  "fecha de nacimiento": "date of birth",
  "sexo": "sex",
  "correo": "email",
  "teléfono": "phone",
  "si fuma": "tobacco use",
  "estado": "state",
  "código postal": "ZIP code",
  "dirección": "address",
  "tamaño del hogar": "household size",
  "ingreso anual": "annual income",
};

const dia = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  // Partes LOCALES, nunca toISOString: en UTC el día se corre de madrugada.
  return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export function HealthSherpaInscribirView({ lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);

  const [conn, setConn] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState("");
  const [elegido, setElegido] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  // Las ETIQUETAS de los datos de la ficha que viajarían a HealthSherpa.
  // Nunca los valores: es para que el agente sepa qué sale del CRM.
  const [prerelleno, setPrerelleno] = useState([]);
  // Y lo que NO va a viajar, con el motivo. Esto es lo que el agente puede
  // arreglar antes de crear el enlace.
  const [faltante, setFaltante] = useState([]);
  // A nombre de QUE AGENTE saldría: el dueño del cliente. Lo que importa
  // cuando quien teclea es un asiento que trabaja los libros de dos agentes.
  const [aNombreDe, setANombreDe] = useState(null);
  const [flujo, setFlujo] = useState("agent_assisted");
  const [anio, setAnio] = useState(new Date().getFullYear() + 1);
  const [consiente, setConsiente] = useState(false);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState("");
  const [copiado, setCopiado] = useState("");

  useEffect(() => {
    getHealthSherpaConnection().then(setConn).catch(() => setConn(null));
  }, []);

  // La búsqueda va al servidor: la cartera puede tener miles y traerlos todos
  // al navegador para filtrar sería lento y, además, PHI de más en memoria.
  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) { setClientes([]); return; }
    let vivo = true;
    const id = setTimeout(() => {
      listClients({ search: q, limit: 20 })
        .then((r) => { if (vivo) setClientes(r?.clients || r?.data || r || []); })
        .catch(() => { if (vivo) setClientes([]); });
    }, 300);
    return () => { vivo = false; clearTimeout(id); };
  }, [busca]);

  const cargarSesiones = useCallback((clientId) => {
    getHealthSherpaEnrollmentSessions(clientId)
      .then((r) => {
        setSesiones(r.sesiones); setPrerelleno(r.prerelleno);
        setFaltante(r.faltante); setANombreDe(r.aNombreDe);
      })
      .catch(() => { setSesiones([]); setPrerelleno([]); setFaltante([]); setANombreDe(null); });
  }, []);

  const elegir = (c) => {
    setElegido(c);
    setClientes([]);
    setBusca("");
    setConsiente(false);
    setError("");
    cargarSesiones(c.id);
  };

  // Llegada desde la ficha del cliente ("Inscribir en HealthSherpa"): el
  // cliente viene ya elegido. Se mira al montar (la pantalla se acaba de abrir)
  // y al oír el aviso (ya estaba abierta). Ver enlacesDeLaFicha.js.
  useEffect(() => {
    const recoger = () => {
      const c = tomarPendiente(PENDIENTE_HEALTHSHERPA);
      if (c && c.id) elegir(c);
    };
    recoger();
    const h = (e) => { if (e?.detail?.nombre === PENDIENTE_HEALTHSHERPA) recoger(); };
    window.addEventListener(EVENTO_PENDIENTE, h);
    return () => window.removeEventListener(EVENTO_PENDIENTE, h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anios = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y + 1];
  }, []);

  const card = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, padding: "1.1rem", marginBottom: "1rem",
  };
  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px",
    background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 14,
    fontWeight: 600, cursor: "pointer",
  });
  const inp = {
    width: "100%", padding: "9px 11px", borderRadius: 8, fontSize: 14,
    background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1c3a4f)", boxSizing: "border-box",
  };
  const lbl = { display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 5, color: "var(--c-text2,#7aa3c0)" };
  const help = { fontSize: 11.5, color: "var(--c-text3,#45688a)", marginTop: 5 };

  const copiar = (texto, cual) => {
    try {
      navigator.clipboard.writeText(texto);
      setCopiado(cual);
      setTimeout(() => setCopiado(""), 2000);
    } catch { /* el enlace se ve igual al lado */ }
  };

  const crear = async () => {
    setError(""); setCreando(true);
    try {
      const s = await createHealthSherpaEnrollmentSession({
        clientId: elegido.id, flujo, planYear: anio,
      });
      toast.success(t("Enlace creado.", "Link created."));
      setSesiones((prev) => [s, ...prev]);
      setConsiente(false);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setCreando(false);
    }
  };

  // LO QUE DECIDE SI SE PUEDE CREAR ES LA CONEXION DEL AGENTE DUEÑO DEL
  // CLIENTE, no la de quien está mirando. Un asiento no tiene cuenta propia
  // en HealthSherpa: la inscripción sale a nombre del agente del cliente y
  // es SU llave la que viaja. Para un agente que inscribe a su propio
  // cliente las dos cosas son la misma, y no cambia nada.
  const miConexion = conn?.state === "connected";
  const puedeCrear = elegido ? !!aNombreDe?.conectado : miConexion;

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <HeartPulse size={22} color="#00d4ff" />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          {t("Inscribir en HealthSherpa", "Enroll in HealthSherpa")}
        </h2>
      </div>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: 13.5, marginTop: 0, marginBottom: "1.25rem", maxWidth: 640 }}>
        {t(
          "Deskte genera el enlace de inscripción de HealthSherpa. La póliza que salga de ahí queda emparejada sola con este cliente: no hay que emparejarla después.",
          "Deskte generates the HealthSherpa enrollment link. The policy that comes out of it is matched to this client on its own: no matching afterwards.",
        )}
      </p>

      {/* Este cartel es sobre TU cuenta, así que solo sale mientras no hay
          cliente elegido. Con uno elegido manda la conexión de SU agente, y
          eso se dice abajo con su nombre — un asiento no tiene cuenta propia
          que conectar y mandarlo a conectarla sería mandarlo a nada. */}
      {!elegido && !miConexion && (
        <div style={{ ...card, background: "rgba(251,191,36,.08)", borderColor: "rgba(251,191,36,.3)", fontSize: 13, lineHeight: 1.55 }}>
          {t(
            "Tu cuenta de HealthSherpa no está conectada todavía. Si eres agente, ve a «HealthSherpa» en el menú y conéctala. Si eres asistente, la inscripción sale con la cuenta del agente dueño del cliente — elige el cliente y aquí te dirá de quién.",
            "Your HealthSherpa account isn't connected yet. If you're an agent, go to «HealthSherpa» in the menu and connect it. If you're an assistant, the enrollment goes out under the account of the client's own agent — pick the client and this will tell you whose.",
          )}
        </div>
      )}

      {error && <div style={{ ...card, background: "#3a1620", borderColor: "#7f1d1d", color: "#fecaca" }}>{error}</div>}

      <div style={card}>
        <label style={lbl}>{t("¿Para qué cliente?", "Which client?")}</label>
        {elegido ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {[elegido.firstName, elegido.lastName].filter(Boolean).join(" ")}
              {elegido.state && (
                <span style={{ fontSize: 13, fontWeight: 400, color: "var(--c-text2,#7aa3c0)" }}>
                  {" · "}{elegido.state}
                </span>
              )}
            </div>
            <button onClick={() => { setElegido(null); setSesiones([]); setPrerelleno([]); setFaltante([]); setANombreDe(null); }} style={btn("transparent", "#7aa3c0")}>
              {t("Cambiar", "Change")}
            </button>
          </div>
        ) : (
          <>
            <input style={inp} value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder={t("Escribe el nombre del cliente…", "Type the client's name…")} />
            {clientes.length > 0 && (
              <div style={{ display: "grid", gap: 5, marginTop: 8 }}>
                {clientes.map((c) => (
                  <button key={c.id} onClick={() => elegir(c)} style={{
                    textAlign: "left", padding: "8px 11px", borderRadius: 8, fontSize: 13.5,
                    background: "var(--c-surface2,#0b1c2b)", color: "var(--c-white,#eef6ff)",
                    border: "1px solid var(--c-border,#1c3a4f)", cursor: "pointer", fontFamily: "inherit",
                  }}>
                    {[c.firstName, c.lastName].filter(Boolean).join(" ")}
                    {c.state && <span style={{ color: "var(--c-text2,#7aa3c0)" }}>{" · "}{c.state}</span>}
                  </button>
                ))}
              </div>
            )}
            {busca.trim().length >= 2 && clientes.length === 0 && (
              <div style={help}>{t("Ningún cliente con ese nombre.", "No client with that name.")}</div>
            )}
          </>
        )}
      </div>

      {elegido && (
        <>
          <div style={card}>
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <label style={lbl}>{t("¿Quién completa la solicitud?", "Who completes the application?")}</label>
                <div style={{ display: "grid", gap: 7 }}>
                  {[
                    { v: "agent_assisted", es: "Yo, acompañando al cliente", en: "Me, with the client" },
                    { v: "self_service", es: "El cliente solo, por un enlace que le mando", en: "The client alone, through a link I send" },
                  ].map((o) => (
                    <label key={o.v} style={{
                      display: "flex", gap: 10, alignItems: "center", cursor: "pointer",
                      padding: "9px 11px", borderRadius: 8, fontSize: 13.5,
                      background: flujo === o.v ? "rgba(0,212,255,.08)" : "var(--c-surface2,#0b1c2b)",
                      border: `1px solid ${flujo === o.v ? "#00d4ff" : "var(--c-border,#1c3a4f)"}`,
                    }}>
                      <input type="radio" name="flujo" checked={flujo === o.v} onChange={() => setFlujo(o.v)} />
                      {t(o.es, o.en)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={lbl}>{t("Año del plan", "Plan year")}</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {anios.map((y) => (
                    <button key={y} onClick={() => setAnio(y)} style={{
                      ...btn(anio === y ? "#00d4ff" : "transparent", anio === y ? "#04121c" : "#7aa3c0"),
                      border: `1px solid ${anio === y ? "#00d4ff" : "var(--c-border,#1c3a4f)"}`,
                    }}>{y}</button>
                  ))}
                </div>
              </div>

              {/* A NOMBRE DE QUIÉN SALE. Es lo primero del bloque y lleva
                  el nombre del agente porque decide QUIÉN COBRA: HealthSherpa
                  acredita la solicitud a la cuenta cuya llave se usó, y esa
                  es siempre la del agente dueño del cliente.
                  Un asiento que trabaja los libros de dos agentes no elige
                  nada — la ficha ya lo dice — pero tiene que poder verlo
                  antes de pulsar. */}
              {aNombreDe && (
                <div style={{
                  padding: "10px 12px", borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
                  background: aNombreDe.conectado ? "rgba(0,212,255,.07)" : "rgba(251,191,36,.08)",
                  border: `1px solid ${aNombreDe.conectado ? "rgba(0,212,255,.25)" : "rgba(251,191,36,.3)"}`,
                }}>
                  {aNombreDe.conectado ? (
                    <>
                      <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
                        {t("Se enviará a nombre de:", "It will go out under:")}
                      </span>{" "}
                      <strong>{aNombreDe.nombre}</strong>
                      <div style={{ color: "var(--c-text3,#45688a)", marginTop: 3 }}>
                        {t(
                          "Es el agente dueño de este cliente, y HealthSherpa le acreditará la solicitud a él. Si no es quien debería, cambia el agente en la ficha del cliente.",
                          "That's the agent who owns this client, and HealthSherpa will credit the application to them. If that's not who it should be, change the agent on the client's record.",
                        )}
                      </div>
                    </>
                  ) : (
                    <span style={{ color: "#fbbf24" }}>
                      {aNombreDe.existe
                        ? t(
                            `${aNombreDe.nombre} todavía no ha conectado su cuenta de HealthSherpa. Esta inscripción saldría a su nombre, así que tiene que conectarla desde su perfil antes.`,
                            `${aNombreDe.nombre} hasn't connected their HealthSherpa account yet. This enrollment would go out under their name, so they need to connect it from their profile first.`,
                          )
                        : t(
                            "No se pudo encontrar al agente dueño de este cliente. Revisa la ficha.",
                            "Couldn't find the agent who owns this client. Check the record.",
                          )}
                    </span>
                  )}
                </div>
              )}

              {/* QUÉ DATOS VIAJAN, dicho antes de que el agente firme el
                  consentimiento y no después. HealthSherpa acepta prerellenar
                  la solicitud con la ficha del cliente, y eso es justo lo que
                  ahorra teclear — pero el agente tiene que poder contestar
                  "¿qué mandas de mí?" sin adivinar. Son etiquetas, no valores:
                  esta pantalla no vuelve a enseñar la ficha. */}
              <div style={{
                padding: "10px 12px", borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
                background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)",
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {t("Lo que Deskte va a prerellenar", "What Deskte will prefill")}
                </div>
                {prerelleno.length > 0 ? (
                  <div style={{ color: "var(--c-text2,#7aa3c0)" }}>
                    {prerelleno.map((p) => (lang === "es" ? p : (PRERELLENO_EN[p] || p))).join(" · ")}
                  </div>
                ) : (
                  <div style={{ color: "var(--c-text3,#45688a)" }}>
                    {t(
                      "Nada: esta ficha no tiene todavía datos que HealthSherpa acepte. El enlace funciona igual, el cliente lo escribe él.",
                      "Nothing: this record has no data HealthSherpa accepts yet. The link still works, the client types it in.",
                    )}
                  </div>
                )}

                {/* Y LO QUE FALTA. Lissette: "si algún dato no está, como
                    fuma, por qué no dar un aviso". Tenía razón: enseñar solo
                    lo que sí viaja deja al cliente topándose con el hueco en
                    mitad del formulario de HealthSherpa. El motivo importa —
                    un ingreso escrito «40k-50k» está en la ficha y parece
                    completo, pero no viaja, y eso nadie lo adivina. */}
                {faltante.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 9, borderTop: "1px solid var(--c-border,#1c3a4f)" }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: "#fbbf24" }}>
                      {t("Lo que NO se va a prerellenar", "What will NOT be prefilled")}
                    </div>
                    <div style={{ display: "grid", gap: 3, color: "var(--c-text2,#7aa3c0)" }}>
                      {faltante.map((f) => (
                        <div key={f.campo}>
                          {lang === "es" ? f.campo : (PRERELLENO_EN[f.campo] || f.campo)}
                          {" — "}
                          <span style={{ color: "var(--c-text3,#45688a)" }}>
                            {f.motivo === "formato"
                              ? t("está en la ficha, pero HealthSherpa no lo acepta escrito así",
                                  "it's on the record, but HealthSherpa doesn't accept it written that way")
                              : t("no está en la ficha", "not on the record")}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 6, color: "var(--c-text3,#45688a)" }}>
                      {t(
                        "Puedes completarlo en la ficha del cliente y volver, o dejarlo: el enlace funciona igual y esos campos los escribe el cliente.",
                        "You can fill it in on the client's record and come back, or leave it: the link works either way and the client types those in.",
                      )}
                    </div>
                    {faltante.some((f) => f.campo === "si fuma") && (
                      <div style={{ marginTop: 6, color: "var(--c-text3,#45688a)" }}>
                        {t(
                          "«Si fuma» en blanco significa «no se ha preguntado», no «no fuma». Por eso Deskte no lo manda: dar por hecho que no fuma puede cambiarle la prima.",
                          "A blank «tobacco use» means «not asked», not «doesn't smoke». That's why Deskte doesn't send it: assuming they don't smoke can change their premium.",
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* EL CONSENTIMIENTO NO ES DECORATIVO. Su contrato: "a production
                  enrollment session must serve the real consumer whose data it
                  contains, and documented consumer consent is required before
                  initiating the session". Sin marcar esto no hay sesión, y la
                  fecha queda guardada. */}
              <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, cursor: "pointer" }}>
                <input type="checkbox" checked={consiente} style={{ marginTop: 3 }}
                  onChange={(e) => setConsiente(e.target.checked)} />
                <span>
                  {t(
                    "Este cliente es real y me dio su consentimiento para iniciar su inscripción. Queda registrado con fecha.",
                    "This client is real and gave me their consent to start their enrollment. It's recorded with a date.",
                  )}
                </span>
              </label>

              <div>
                <button onClick={crear} disabled={!consiente || creando || !puedeCrear}
                  style={{ ...btn(consiente && puedeCrear ? "#16a34a" : "transparent", consiente && puedeCrear ? "#fff" : "#45688a") }}>
                  {creando ? <Loader2 size={16} className="dkt-spin" /> : <UserPlus size={16} />}
                  {t("Crear el enlace", "Create the link")}
                </button>
                {!elegido.state && (
                  <div style={{ ...help, color: "#fbbf24" }}>
                    {t(
                      "Este cliente no tiene estado en su ficha, y HealthSherpa lo exige. Ponlo en la ficha y vuelve.",
                      "This client has no state on their record, and HealthSherpa requires it. Add it on the record and come back.",
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {sesiones.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                {t("Enlaces de este cliente", "This client's links")}
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {sesiones.map((s) => (
                  <div key={s.id} style={{
                    padding: "10px 12px", borderRadius: 8,
                    background: "var(--c-surface2,#0b1c2b)", border: "1px solid var(--c-border,#1c3a4f)",
                  }}>
                    <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", marginBottom: 7 }}>
                      {s.planYear} · {s.flow === "self_service" ? t("el cliente solo", "client alone") : t("acompañado", "agent-assisted")}
                      {" · "}{dia(s.createdAt)}
                    </div>
                    {/* EL NUMERO DE CONFIRMACION DE HEALTHSHERPA, cuando ya
                        apareció. No es el identificador nuestro: este lo
                        genera HealthSherpa cuando el cliente completa la
                        solicitud, y es el que la agente busca en su portal.
                        Se anota en la sesión al abrir «Emparejar». */}
                    {s.hsConfirmationId && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 7, fontSize: 12.5 }}>
                        <span style={{ color: "#4ade80" }}>{t("Ya inscrita", "Already enrolled")}</span>
                        <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
                          {t("N.º de confirmación de HealthSherpa:", "HealthSherpa confirmation no.:")}
                        </span>
                        <code style={{ fontSize: 12.5 }}>{s.hsConfirmationId}</code>
                        <button onClick={() => copiar(s.hsConfirmationId, s.hsConfirmationId)}
                          style={{ ...btn("transparent", "#00d4ff"), padding: "4px 9px", fontSize: 12.5 }}>
                          {copiado === s.hsConfirmationId ? <Check size={12} /> : <Copy size={12} />}
                          {copiado === s.hsConfirmationId ? t("Copiado", "Copied") : t("Copiar", "Copy")}
                        </button>
                      </div>
                    )}
                    {[
                      { url: s.clientApplyUrl, es: "Enlace para el cliente", en: "Link for the client" },
                      { url: s.shoppingUrl, es: "Enlace de compra", en: "Shopping link" },
                    ].filter((x) => x.url).map((x) => (
                      <div key={x.url} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 5 }}>
                        <span style={{ fontSize: 12.5, minWidth: 140 }}>{t(x.es, x.en)}</span>
                        <button onClick={() => copiar(x.url, x.url)} style={{ ...btn("transparent", "#00d4ff"), padding: "4px 9px", fontSize: 12.5 }}>
                          {copiado === x.url ? <Check size={12} /> : <Copy size={12} />}
                          {copiado === x.url ? t("Copiado", "Copied") : t("Copiar", "Copy")}
                        </button>
                        <a href={x.url} target="_blank" rel="noreferrer"
                          style={{ ...btn("transparent", "#7aa3c0"), padding: "4px 9px", fontSize: 12.5, textDecoration: "none" }}>
                          <ExternalLink size={12} /> {t("Abrir", "Open")}
                        </a>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Lo que esta pantalla NO hace, dicho antes de que nadie lo pregunte. */}
      <div style={{ ...card, background: "transparent", borderStyle: "dashed" }}>
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
          <AlertTriangle size={16} color="#7aa3c0" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 12.5, color: "var(--c-text2,#7aa3c0)", lineHeight: 1.6 }}>
            {t(
              "La inscripción se hace en HealthSherpa, no dentro de Deskte: la aplicación ACA está regulada por CMS y tiene que correr en una plataforma aprobada. El enlace sale prerrellenado solo con lo que dice arriba; lo demás se completa en HealthSherpa.",
              "Enrollment happens at HealthSherpa, not inside Deskte: the ACA application is regulated by CMS and has to run on an approved platform. The link is prefilled only with what's listed above; the rest is filled in at HealthSherpa.",
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
